import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from './i18n/LanguageContext';
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import WhySmarterSection from './components/WhySmarterSection';
import PlatformSection from './components/PlatformSection';
import WhyNowSection from './components/WhyNowSection';
import RoadmapSection from './components/RoadmapSection';
import FounderSection from './components/FounderSection';
import ResearchSection from './components/ResearchSection';
import ExpressInterestSection from './components/ExpressInterestSection';
import Footer from './components/Footer';
import MyceliumCanvas from './components/MyceliumCanvas';

/* Intertwining wave divider — light travels through the stroke lines themselves */
function SectionDivider() {
  /* Each wave path has a dim base layer + an animated bright layer.
     stroke-dasharray/dashoffset makes a bright "dash" travel left→right
     along each path. Staggered delays give a slightly organic, flowing feel. */
  const waves = [
    { d: "M0,60 C80,45 160,85 240,55 C320,25 400,60 480,50 C560,40 640,90 720,60 L800,70",  baseOpacity: 0.14, sw: 0.8,  delay: 0    },
    { d: "M0,62 C100,50 180,95 260,65 C340,35 420,70 500,60 C580,50 660,100 740,70 L800,78", baseOpacity: 0.14, sw: 0.8,  delay: 0.12 },
    { d: "M0,58 C60,40 140,80 220,50 C300,20 380,55 460,45 C540,35 620,85 700,55 L800,62",  baseOpacity: 0.14, sw: 0.8,  delay: 0.06 },
    { d: "M0,56 C70,38 150,78 230,48 C310,18 390,53 470,43 C550,33 630,83 710,53 L800,60",  baseOpacity: 0.14, sw: 0.8,  delay: 0.18 },
    { d: "M0,64 C90,52 170,90 250,60 C330,30 410,65 490,55 C570,45 650,95 730,65 L800,75",  baseOpacity: 0.10, sw: 0.8,  delay: 0.09 },
    { d: "M0,66 C110,54 190,92 270,62 C350,32 430,68 510,58 C590,48 670,98 750,68 L800,72", baseOpacity: 0.07, sw: 0.8,  delay: 0.22 },
    { d: "M0,50 C120,30 200,90 280,60 C360,30 440,80 520,70 L800,55",                       baseOpacity: 0.05, sw: 0.5,  delay: 0.15 },
    { d: "M0,70 C120,90 200,30 280,60 C360,90 440,40 520,50 L800,65",                       baseOpacity: 0.05, sw: 0.5,  delay: 0.03 },
    { d: "M0,60 C75,50 155,75 235,50 C315,25 395,65 475,55 C555,45 635,80 715,60 L800,67",  baseOpacity: 0.09, sw: 0.6,  delay: 0.25 },
  ];

  /* Scattered dots — fixed positions that feel naturally distributed */
  const dots = [
    { cx: 88,  cy: 52, r: 1.3, opacity: 0.35 },
    { cx: 215, cy: 78, r: 1.5, opacity: 0.30 },
    { cx: 370, cy: 40, r: 1.1, opacity: 0.28 },
    { cx: 492, cy: 88, r: 1.4, opacity: 0.32 },
    { cx: 624, cy: 50, r: 1.2, opacity: 0.30 },
    { cx: 748, cy: 72, r: 1.3, opacity: 0.28 },
    { cx: 148, cy: 36, r: 1.0, opacity: 0.25 },
    { cx: 558, cy: 96, r: 1.1, opacity: 0.27 },
  ];

  return (
    <div className="relative w-full overflow-hidden" style={{ height: '72px' }}>
      <svg
        viewBox="0 0 800 72"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        strokeLinecap="round"
        fill="none"
        aria-hidden="true"
      >
        {waves.map((w, i) => (
          <React.Fragment key={i}>
            {/* Dim static base line */}
            <path
              d={w.d}
              stroke={`rgba(189,214,181,${w.baseOpacity})`}
              strokeWidth={w.sw}
            />
            {/* Animated green dash traveling along the line */}
            <path
              d={w.d}
              stroke="rgba(189,214,181,0.52)"
              strokeWidth={w.sw + 0.4}
              className="wave-line-animated"
              style={{ animationDelay: `${w.delay}s` }}
            />
          </React.Fragment>
        ))}

        {/* Scattered accent dots */}
        {dots.map((dot, i) => (
          <circle
            key={`dot-${i}`}
            cx={dot.cx}
            cy={dot.cy}
            r={dot.r}
            fill={`rgba(189,214,181,${dot.opacity})`}
          />
        ))}
      </svg>

      {/* Soft edge fade so waves dissolve into the page */}
      <div className="absolute inset-y-0 left-0 w-16 pointer-events-none"  style={{ background: 'linear-gradient(to right, rgba(8,11,8,0.9), transparent)' }} />
      <div className="absolute inset-y-0 right-0 w-16 pointer-events-none" style={{ background: 'linear-gradient(to left,  rgba(5,7,5,0.9),  transparent)' }} />
    </div>
  );
}

const LandingPage = () => {
  return (
    <div
      className="relative min-h-screen forest-page-bg"
      data-testid="landing-page"
    >
      {/* Animated mycelium network background */}
      <MyceliumCanvas />

      {/* Navigation */}
      <Navigation />

      {/* Main content */}
      <main className="relative z-[2]">
        <HeroSection />
        <SectionDivider />
        <ProblemSection />
        <SectionDivider />
        <SolutionSection />
        <SectionDivider />
        <WhySmarterSection />
        <SectionDivider />
        <PlatformSection />
        <SectionDivider />
        <RoadmapSection />
        <ExpressInterestSection />
        <SectionDivider />
        <FounderSection />
      </main>

      <Footer />
    </div>
  );
};

/* Shared wrapper for standalone pages */
function StandalonePage({ children }) {
  return (
    <div className="relative min-h-screen forest-page-bg">
      <MyceliumCanvas />
      <Navigation />
      <main className="relative z-[2] pt-24">
        {children}
      </main>
      <Footer />
    </div>
  );
}

const ResearchPage = () => (
  <StandalonePage>
    <ResearchSection />
    <WhyNowSection />
  </StandalonePage>
);

const LoginPlaceholder = () => (
  <div className="min-h-screen bg-[#0E110E] flex items-center justify-center px-6" data-testid="login-page">
    <div className="glass-card p-12 max-w-md w-full text-center">
      <div className="flex justify-center mb-6">
        <svg width="48" height="48" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="100" cy="100" rx="88" ry="90" stroke="#98B091" strokeWidth="2" fill="none" opacity="0.6"/>
          <path d="M100 35 C138 33, 165 60, 168 100 C170 138, 142 168, 100 170 C62 171, 35 142, 33 100 C31 62, 58 35, 100 35Z" stroke="#98B091" strokeWidth="1.8" fill="none" opacity="0.6"/>
          <path d="M100 65 C124 63, 138 78, 140 100 C142 122, 126 140, 100 141 C78 142, 62 126, 61 100 C60 78, 74 65, 100 65Z" stroke="#98B091" strokeWidth="1.8" fill="none" opacity="0.8"/>
          <path d="M100 80 C114 79, 122 88, 123 100 C124 112, 115 123, 100 124 C88 124, 79 115, 78 100 C77 88, 86 80, 100 80Z" stroke="#98B091" strokeWidth="2" fill="none" opacity="0.9"/>
        </svg>
      </div>
      <h1 className="text-2xl font-medium text-white mb-2">Dashboard Access</h1>
      <p className="text-sm text-white/40 mb-8">The Canopi dashboard is coming online for PNW pilots in Summer 2026. Sign up on the homepage to be first in.</p>
      <a href="/" data-testid="login-back-button" className="inline-block bg-[#3D5638] hover:bg-[#4A6845] text-white rounded-full px-8 py-3 font-medium transition-all duration-300 shadow-[0_0_15px_rgba(61,86,56,0.3)]">
        Back to Home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/login" element={<LoginPlaceholder />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
